import * as React from 'react';
import { Redirect } from 'react-router';

import { Box, Button, Image, Link, Text } from '@opendoor/bricks-next';

import { PageContent, PageHeader, PageLayout } from 'src/components/layouts/PageLayout';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import { addressEntryUrl, WEB_API_URL } from 'src/helpers/globals';
import { Session } from 'src/helpers/session';
import { isOpendoorAppEvoWebView } from 'src/helpers/webview';
import { offerDashboardPath } from 'src/routes/pathBuilders';
import { useGetHumanQuery, useLazyVerifyActiveOfferQuery } from 'src/store/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { removeHuman } from 'src/store/sellerFlow/actions';

import AddressLine from '../../components/common/AddressLine';
import ErrorNotice from '../../components/common/ErrorNotice';
import { getLeadToken } from '../../store/router/selectors';
import {
  address as getAddress,
  getAnswers,
  channel as getChannel,
  denialCode as getDenialCode,
} from '../../store/sellerFlow/selectors';
import { GeneralDenial } from './GeneralDenial';

const LISTED_CHANNELS = ['seller_request_listed', 'agent_request_listed', 'outbound_offers_listed'];

type DenialHeaderProps = React.PropsWithChildren;

export const DenialHeader: React.FC<DenialHeaderProps> = ({ children }) => (
  <Box mb={15}>
    <Text tag="h4" typography="$subheaderMedium" $largerThanMD={{ typography: '$headerSmall' }}>
      {children}
    </Text>
  </Box>
);

type DenialTextAfterHeaderProps = React.PropsWithChildren;

export const DenialTextAfterHeader: React.FC<DenialTextAfterHeaderProps> = ({ children }) => (
  <Text
    typography="$bodyMedium"
    $largerThanMD={{ typography: '$bodyLarge' }}
    tag="p"
    color="$contentTertiary"
  >
    {children}
  </Text>
);

type DenialCard = React.PropsWithChildren<{
  isOpendoorAppEvoWebView: boolean;
}>;

export const DenialCard: React.FC<DenialCard> = ({ children, isOpendoorAppEvoWebView }) => {
  const imageUrl =
    'https://images.opendoor.com/source/s3/imgdrop-production/2024-07-1719967759519-48851.jpg?preset=square-256&service=cosmos&dpr=2';
  const largeImageUrl =
    'https://images.opendoor.com/source/s3/imgdrop-production/2024-07-1720211332699-21961.jpg?preset=square-1024&service=cosmos&dpr=2';

  if (isOpendoorAppEvoWebView) {
    return (
      <Box
        maxWidth={620}
        mx="auto"
        display="flex"
        flexDirection="column"
        gap="$16x"
        $largerThanMD={{
          gap: '$20x',
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="row" borderRadius="$16x" style={{ overflow: 'hidden' }}>
      <Box
        backgroundColor="$backgroundPrimary"
        flex={1}
        zIndex={1}
        borderRadius="$16x"
        paddingHorizontal="$12x"
        paddingVertical="$20x"
        display="flex"
        gap="$16x"
        $largerThanMD={{
          padding: '$28x',
          gap: '$20x',
        }}
        maxWidth={656}
        style={{
          width: '100%',
        }}
        $smallerThanMD={{
          marginTop: 218,
        }}
      >
        {children}
      </Box>
      <Box
        style={{
          width: '100%',
        }}
        $smallerThanMD={{
          display: 'none',
        }}
        flex={1}
      ></Box>
      <Image
        $smallerThanMD={{
          display: 'none',
        }}
        style={{
          resizeMode: 'cover',
          position: 'absolute',
          flex: 1,
          top: 0,
          bottom: 0,
          left: '40%',
          right: 0,
        }}
        alt="neighborhood"
        source={{
          uri: largeImageUrl,
        }}
      />
      <Image
        display="none"
        $smallerThanMD={{
          display: 'block',
        }}
        style={{
          resizeMode: 'cover',
          position: 'absolute',
          flex: 1,
          top: 0,
          left: 0,
          right: 0,
          height: 256,
        }}
        alt="neighborhood"
        source={{
          uri: imageUrl,
        }}
      />
    </Box>
  );
};

const ActiveOfferDenial = ({ address, email, sellerInputUUID }) => {
  const { data: human, refetch: refetchHuman } = useGetHumanQuery();
  const dispatch = useAppDispatch();

  const [verifyActiveOffer, { data: activeOfferData, isError, isLoading }] =
    useLazyVerifyActiveOfferQuery();

  React.useEffect(() => {
    const emailToUse = human?.email || email;
    verifyActiveOffer({ email: emailToUse, sellerInputUUID });
  }, [email, human?.email, sellerInputUUID, verifyActiveOffer]);

  const handleLogout = async () => {
    try {
      await Session.logOut();
      refetchHuman();
    } catch (error) {
      dispatch(removeHuman());
    }
  };
  const handleSubmit = async () => {
    const addressUUID = address?.uuid;
    const path = offerDashboardPath(addressUUID);

    if (human) {
      if (activeOfferData?.offerDashboardUrl) {
        window.location.assign(activeOfferData.offerDashboardUrl);
        return;
      }
      await handleLogout();
    }
    window.location.href = `${WEB_API_URL}/login?redirectTo=${path}`;
  };

  let header = (
    <Box>
      <DenialHeader>There is an active offer for this home</DenialHeader>
      <DenialTextAfterHeader>
        If someone in your household requested this offer, they can sign into their account and add
        you to it.
      </DenialTextAfterHeader>
    </Box>
  );
  if (human) {
    if (isError) {
      header = (
        <Box>
          <DenialHeader>This offer is linked to another Opendoor account</DenialHeader>
          <DenialTextAfterHeader>
            Try signing into Opendoor with another account to view it.
          </DenialTextAfterHeader>
        </Box>
      );
    } else {
      header = <DenialHeader>There is an active offer for this home</DenialHeader>;
    }
  }

  let ctaLabel = 'Sign in to view offer';
  if (human) {
    if (isError) {
      ctaLabel = 'Sign into another account';
    } else {
      ctaLabel = 'View offer';
    }
  }

  return (
    <>
      {header}
      <Box>
        <Box>
          <Button
            label={ctaLabel}
            onPress={handleSubmit}
            analyticsName="reception-fe-denied-view-latest-offer"
            aria-label={undefined}
            loading={isLoading}
            size="small"
            variant="primary"
            width="100%"
          />
        </Box>
      </Box>
      <AddressLine address={address} />
      <ErrorNotice />
    </>
  );
};

const InNetworkDenial = ({ address }) => {
  const inNetworkHeader = 'This home is currently owned by Opendoor';
  return (
    <>
      <Box>
        <DenialHeader>{inNetworkHeader}</DenialHeader>
        <DenialTextAfterHeader>
          We already purchased this home. If you're looking for an offer, try another address.
        </DenialTextAfterHeader>
      </Box>
      <Box>
        <Button
          label="Enter new address"
          href={addressEntryUrl}
          size="small"
          variant="primary"
          analyticsName="reception-fe-denied-address-entry"
          aria-label=""
          width="100%"
        />
      </Box>
      <AddressLine address={address} />
      <ErrorNotice />
    </>
  );
};

const FlowDenied: React.FC = () => {
  const leadToken = useAppSelector(getLeadToken);
  const answers = useAppSelector(getAnswers);
  const channel = useAppSelector(getChannel);
  const leadDenialCode = useAppSelector(getDenialCode);
  const address = useAppSelector(getAddress);

  const denialCode = leadDenialCode ?? '';

  const flipStateDenialCodes = [
    'offer_in_progress',
    'owned_by_opendoor',
    'listed_by_opendoor',
    'in_contract_with_opendoor',
  ];
  const isDeniedOnFlipStates = flipStateDenialCodes.includes(denialCode);

  let sellerEmail = '';
  if (typeof answers['seller.email'] === 'string') {
    sellerEmail = answers['seller.email'];
  }

  const getMainContent = () => {
    if (denialCode === 'active_offer') {
      if (LISTED_CHANNELS.includes(channel)) {
        return <Redirect to={`/seller-lead/${leadToken}/p/verifying-listing`} />;
      }
      return (
        <ActiveOfferDenial address={address} email={sellerEmail} sellerInputUUID={leadToken} />
      );
    } else if (isDeniedOnFlipStates) {
      return <InNetworkDenial address={address} />;
    }

    return (
      <GeneralDenial
        address={address}
        denialCode={denialCode}
        leadToken={leadToken}
        sellerEmail={sellerEmail}
      />
    );
  };

  return (
    <DenialCard isOpendoorAppEvoWebView={isOpendoorAppEvoWebView}>{getMainContent()}</DenialCard>
  );
};

const FlowDeniedWithLoader = () => (
  <ReceptionLoader>
    <PageLayout>
      <PageHeader>
        <Link
          href={WEB_API_URL}
          aria-label="Exit"
          analyticsName="reception-fe-exit"
          typography={'$labelMedium'}
        >
          Exit
        </Link>
      </PageHeader>
      <PageContent>
        <FlowDenied />
      </PageContent>
    </PageLayout>
  </ReceptionLoader>
);

export default FlowDeniedWithLoader;
